// @generated-imports
import { registerAppLayers } from './app-layers.js';
import { checkCrashRecovery } from './autosave.js';
import { closeBackupModal, exportBackup, openBackupModal, startImportFlow } from './backup.js';
import { canShareFiles, handleBackupFolderButton, maybeAutoFolderBackup, shareBackup } from './backupplus.js';
import { closeCollectionsPanel, handleCollectionsClick, matchesListFilter, openCollectionsPanel, renderListFilterOptions, togglePlayLater, updateGameModalExtras } from './collections.js';
import { CONFIG, dom, fallbackRoms, state } from './core.js';
import { armButtonConfirm, clearAppCache, handleConfigClick, handleEmuDataClick, wipeEmulatorData } from './datapanel.js';
import { closeDeviceCheck, copyDeviceCheck, openDeviceCheck } from './devicecheck.js';
import { closeWhatsNew, initUiScale, maybeShowWhatsNew, openWhatsNew } from './display.js';
import { LOCAL_CORE_LABELS, handleDroppedRomFile, setupLocalRomDragAndDrop } from './dragdrop.js';
import { announce, currentGpCard, moveGpFocus, registerServiceWorker, setupGamepadNavigation } from './gamepad.js';
import { cancelPrompt, isLayerOpen, layerClosed, layerOpened, openConfirmLayer, submitPrompt } from './layers.js';
import { closeImportQueue, closeLibraryEdit, continueLocalRom, exportLibraryMetadata, importFilesBatch, importLibraryMetadata, isLocalRomId, loadLocalRomLibrary, localRomAsGame, openLibraryEdit, removeLocalRomEntry, removeLocalRomsBatch, submitLibraryEdit } from './library.js';
import { initLiteMode, setupModalFocusTrap, smoothBehavior } from './lite.js';
import { matchesPlayersFilter, openPlayerAssignment, shouldAskPlayerOrder } from './multiplayer.js';
import { describeOfflineStatus, handleOfflineListClick, isGameOffline, toggleGameOffline, verifyOfflineIndex } from './offline.js';
import { closePadTest, openPadTest } from './padtest.js';
import { handlePauseClick, openPauseMenu } from './pausemenu.js';
import { closeHelpModal, closeSettingsModal, formatPlayTime, getPlayTime, handleLaunchAction, handleSettingsClick, hasSaveFor, heroPlayAction, initGlobalErrorHandler, initInstallPrompt, initShowcase, loadPlayTime, loadSettings, maybeRemindBackup, openHelpModal, openSettingsModal, playRandomGame, promptInstall, refreshSaveIndex, setStateButtonsVisible, shareCurrentGame, updateHeroCta } from './platform.js';
import { getPlayCount, isFavorite, loadFavorites, loadPlayCounts, loadRecentGames, persistFilters, renderRecentGames, restoreFilters, toggleFavorite } from './prefs.js';
import { applyActiveProfile, closeProfilesPanel, handleProfilesClick, maybeAskProfile, openProfilesPanel, renderProfileChip } from './profiles.js';
import { continueResumeInfo, handleResumeClick, renderResumeRow } from './resume.js';
import { endSession } from './session.js';
import { closeStatesPanel, handleStatesClick, importStateFile, openStatesPanel, quickLoadCurrent, quickSaveCurrent, refreshStateMeta, startGameWithResume } from './states.js';
import { closeStatsPanel, evaluateAchievements, handleStatsClick, openStatsPanel } from './stats.js';
// @generated-imports
document.addEventListener('DOMContentLoaded', init);

document.addEventListener('fullscreenchange', updateFullscreenButton);

document.addEventListener('webkitfullscreenchange', updateFullscreenButton);

let initialized = false;

function init() {
  if (initialized) return; // proteção contra dupla inicialização
  initialized = true;
  // Perfil PRIMEIRO: reescreve as chaves do CONFIG antes de qualquer leitura.
  applyActiveProfile();
  cacheDom();
  loadFavorites();
  loadRecentGames();
  loadPlayCounts();
  restoreFilters();
  loadSettings();
  loadPlayTime();
  initUiScale();
  bindEvents();
  registerAppLayers();
  renderProfileChip();
  initLiteMode();
  initInstallPrompt();
  initGlobalErrorHandler();
  initShowcase();
  renderCatalogSkeleton();
  updateFullscreenButton();
  setupGamepadNavigation();
  setupModalFocusTrap();
  registerServiceWorker();

  // Catálogo remoto e biblioteca local são fontes INDEPENDENTES; a lista
  // exibida é derivada (recomposeGames) em qualquer ordem de conclusão.
  // Ações de abertura (?action=, ?game=, atalhos do PWA) só rodam depois
  // que as duas fontes estão prontas — antes, "continue" com catálogo
  // vazio apagava o último jogo e "random" não tinha candidatos.
  state.bootReady = false;
  Promise.all([
    loadCatalog(),
    loadLocalRomLibrary().catch(() => undefined),
    refreshStateMeta().catch(() => undefined),
  ]).then(() => {
    state.bootReady = true;
    recomposeGames();
    renderRecentGames();
    renderListFilterOptions();
    renderRegionOptions();
    updateContinueButton();
    updateHeroCta();
    renderResumeRow();
    refreshSaveIndex().then(() => applyFiltersAndRender());
    verifyOfflineIndex().then((dropped) => { if (dropped) { renderListFilterOptions(); applyFiltersAndRender(); } });
    evaluateAchievements();
    maybeRemindBackup();
    openDeepLinkedGame();
    handleLaunchAction();
    runBootPrompts();
  });
}

/** Perguntas do boot em sequência (nunca empilhadas): perfil → novidades →
 *  recuperação de partida interrompida → backup automático na pasta. */
function runBootPrompts() {
  const rest = () => {
    maybeShowWhatsNew();
    whenLayerClosed('whats-new', () => {
      checkCrashRecovery();
      maybeAutoFolderBackup();
    });
  };
  if (maybeAskProfile()) whenLayerClosed('profiles', rest);
  else rest();
}

function whenLayerClosed(id, fn) {
  if (!isLayerOpen(id)) { fn(); return; }
  const timer = window.setInterval(() => {
    if (!isLayerOpen(id)) { window.clearInterval(timer); fn(); }
  }, 250);
}

const REGION_LABELS = { US: 'EUA', EU: 'Europa', JP: 'Japão', BR: 'Brasil', World: 'Mundial' };

function isPortugueseGame(game) {
  const tags = (game.tags || []).map((t) => String(t).toLowerCase());
  return game.region === 'BR' || tags.some((t) => t === 'pt-br' || t === 'português' || t === 'portugues' || t.indexOf('tradução') === 0 || t.indexOf('traduzido') === 0);
}

function matchesRegionFilter(game) {
  const filter = state.regionFilter || 'all';
  if (filter === 'all') return true;
  if (filter === 'pt') return isPortugueseGame(game);
  return game.region === filter;
}

function renderRegionOptions() {
  if (!dom.regionFilter) return;
  const regions = [...new Set(state.roms.map((g) => g.region).filter(Boolean))].sort();
  const pt = state.roms.filter(isPortugueseGame).length;
  const options = [['all', 'Todas']].concat(pt ? [['pt', `🇧🇷 Em português (${pt})`]] : [])
    .concat(regions.map((r) => [r, REGION_LABELS[r] || r]));
  dom.regionFilter.innerHTML = options.map(([v, l]) => `<option value="${escapeAttribute(v)}">${escapeHTML(l)}</option>`).join('');
  dom.regionFilter.value = options.some(([v]) => v === state.regionFilter) ? state.regionFilter : 'all';
  state.regionFilter = dom.regionFilter.value;
}

function cacheDom() {
  dom.continueBtn = document.querySelector('#continueBtn');
  dom.totalGamesStat = document.querySelector('#totalGamesStat');
  dom.genresStat = document.querySelector('#genresStat');
  dom.catalogGrid = document.querySelector('#catalogGrid');
  dom.catalogCount = document.querySelector('#catalogCount');
  dom.catalogError = document.querySelector('#catalogError');
  dom.catalogErrorText = document.querySelector('#catalogErrorText');
  dom.searchInput = document.querySelector('#searchInput');
  dom.searchClearBtn = document.querySelector('#searchClearBtn');
  dom.genreFilter = document.querySelector('#genreFilter');
  dom.systemFilter = document.querySelector('#systemFilter');
  dom.sortSelect = document.querySelector('#sortSelect');
  dom.favoritesToggle = document.querySelector('#favoritesToggle');
  dom.favCountBadge = document.querySelector('#favCountBadge');
  dom.clearFiltersBtn = document.querySelector('#clearFiltersBtn');
  dom.emptyStateTitle = document.querySelector('#emptyStateTitle');
  dom.emptyStateText = document.querySelector('#emptyStateText');
  dom.scrollTopBtn = document.querySelector('#scrollTopBtn');
  dom.siteHeader = document.querySelector('[data-header]');
  dom.viewCoverBtn = document.querySelector('#viewCoverBtn');
  dom.viewShotBtn = document.querySelector('#viewShotBtn');
  dom.gameModal = document.querySelector('#gameModal');
  dom.gameModalClose = document.querySelector('#gameModalClose');
  dom.gameModalImage = document.querySelector('#gameModalImage');
  dom.gameModalBackdrop = document.querySelector('#gameModalBackdrop');
  dom.gameModalTitle = document.querySelector('#gameModalTitle');
  dom.gameModalMeta = document.querySelector('#gameModalMeta');
  dom.gameModalDesc = document.querySelector('#gameModalDesc');
  dom.gameModalPlay = document.querySelector('#gameModalPlay');
  dom.backupBtn = document.querySelector('#backupBtn');
  dom.backupModal = document.querySelector('#backupModal');
  dom.backupClose = document.querySelector('#backupClose');
  dom.backupSummary = document.querySelector('#backupSummary');
  dom.backupExportBtn = document.querySelector('#backupExportBtn');
  dom.backupImportBtn = document.querySelector('#backupImportBtn');
  dom.backupFileInput = document.querySelector('#backupFileInput');
  dom.loadMoreWrap = document.querySelector('#loadMoreWrap');
  dom.loadMoreBtn = document.querySelector('#loadMoreBtn');
  dom.romFileInput = document.querySelector('#romFileInput');
  dom.openRomBtn = document.querySelector('#openRomBtn');
  dom.openRomBtnPlaceholder = document.querySelector('#openRomBtnPlaceholder');
  dom.recentStrip = document.querySelector('#recentStrip');
  dom.recentChips = document.querySelector('#recentChips');
  dom.powerOffBtn = document.querySelector('#powerOffBtn');
  dom.exitRing = document.querySelector('#exitRing');
  dom.exitRingProgress = document.querySelector('#exitRingProgress');
  dom.systemsStat = document.querySelector('#systemsStat');
  dom.emptyState = document.querySelector('#emptyState');
  dom.emptyClearBtn = document.querySelector('#emptyClearBtn');
  dom.playerSection = document.querySelector('#playerSection');
  dom.playerFrame = document.querySelector('#playerFrame');
  dom.playerPlaceholder = document.querySelector('#playerPlaceholder');
  dom.emulatorMount = document.querySelector('#emulatorMount');
  dom.gameLoading = document.querySelector('#gameLoading');
  dom.gameLoadingText = document.querySelector('#gameLoadingText');
  dom.gameError = document.querySelector('#gameError');
  dom.gameErrorText = document.querySelector('#gameErrorText');
  dom.selectedGameTitle = document.querySelector('#selectedGameTitle');
  dom.selectedGameMeta = document.querySelector('#selectedGameMeta');
  dom.backToCatalogBtn = document.querySelector('#backToCatalogBtn');
  dom.fullscreenBtn = document.querySelector('#fullscreenBtn');
  dom.gameErrorBackBtn = document.querySelector('#gameErrorBackBtn');
  dom.toast = document.querySelector('#toast');
  dom.liveRegion = document.querySelector('#liveRegion');
  dom.dropOverlay = document.querySelector('#dropOverlay');
  dom.corePicker = document.querySelector('#corePicker');
  dom.corePickerText = document.querySelector('#corePickerText');
  dom.corePickerGrid = document.querySelector('#corePickerGrid');
  dom.corePickerCancel = document.querySelector('#corePickerCancel');
  dom.gamepadStatus = document.querySelector('#gamepadStatus');
  dom.gamepadStatusText = document.querySelector('#gamepadStatusText');
  dom.localRomsList = document.querySelector('#localRomsList');
  dom.emuDataList = document.querySelector('#emuDataList');
  dom.configList = document.querySelector('#configList');
  dom.helpBtn = document.querySelector('#helpBtn');
  dom.helpBtnPlaceholder = document.querySelector('#helpBtnPlaceholder');
  dom.helpModal = document.querySelector('#helpModal');
  dom.helpClose = document.querySelector('#helpClose');
  dom.helpKeyboard = document.querySelector('#helpKeyboard');
  dom.helpGamepad = document.querySelector('#helpGamepad');
  dom.helpCatalog = document.querySelector('#helpCatalog');
  dom.helpSaves = document.querySelector('#helpSaves');
  dom.settingsBtn = document.querySelector('#settingsBtn');
  dom.settingsModal = document.querySelector('#settingsModal');
  dom.settingsClose = document.querySelector('#settingsClose');
  dom.settingsList = document.querySelector('#settingsList');
  dom.randomBtn = document.querySelector('#randomBtn');
  dom.installBtn = document.querySelector('#installBtn');
  dom.heroPlayBtn = document.querySelector('#heroPlayBtn');
  dom.heroPlayLabel = document.querySelector('#heroPlayLabel');
  dom.saveStateBtn = document.querySelector('#saveStateBtn');
  dom.loadStateBtn = document.querySelector('#loadStateBtn');
  dom.gameModalShare = document.querySelector('#gameModalShare');
  dom.withSaveToggle = document.querySelector('#withSaveToggle');
  dom.saveCountBadge = document.querySelector('#saveCountBadge');
  dom.padLegend = document.querySelector('#padLegend');
  dom.showcase = document.querySelector('#showcase');
  dom.showcaseArt = document.querySelector('#showcaseArt');
  dom.showcaseTitle = document.querySelector('#showcaseTitle');
  dom.showcaseMeta = document.querySelector('#showcaseMeta');
  dom.appVersionLabel = document.querySelector('#appVersionLabel');
  dom.gameErrorActions = document.querySelector('#gameErrorActions');
  dom.statesBtn = document.querySelector('#statesBtn');
  dom.statesModal = document.querySelector('#statesModal');
  dom.statesClose = document.querySelector('#statesClose');
  dom.statesTitle = document.querySelector('#statesTitle');
  dom.statesList = document.querySelector('#statesList');
  dom.confirmLayer = document.querySelector('#confirmLayer');
  dom.confirmTitle = document.querySelector('#confirmTitle');
  dom.confirmMessage = document.querySelector('#confirmMessage');
  dom.confirmActions = document.querySelector('#confirmActions');
  dom.playersModal = document.querySelector('#playersModal');
  dom.playersTitle = document.querySelector('#playersTitle');
  dom.playersHint = document.querySelector('#playersHint');
  dom.playersList = document.querySelector('#playersList');
  dom.playersConfirm = document.querySelector('#playersConfirm');
  dom.playersSkip = document.querySelector('#playersSkip');
  dom.playersFilter = document.querySelector('#playersFilter');
  dom.importQueueModal = document.querySelector('#importQueueModal');
  dom.importQueueList = document.querySelector('#importQueueList');
  dom.importQueueSummary = document.querySelector('#importQueueSummary');
  dom.importQueueClose = document.querySelector('#importQueueClose');
  dom.libraryEditModal = document.querySelector('#libraryEditModal');
  dom.libraryEditTitle = document.querySelector('#libraryEditTitle');
  dom.libraryEditCore = document.querySelector('#libraryEditCore');
  dom.libraryEditCover = document.querySelector('#libraryEditCover');
  dom.libraryEditSave = document.querySelector('#libraryEditSave');
  dom.libraryEditClose = document.querySelector('#libraryEditClose');
  dom.libraryMetaInput = document.querySelector('#libraryMetaInput');
  // v2.1
  ['pauseMenu', 'pauseTitle', 'pauseHint', 'pauseNote', 'pauseActions', 'pauseMenuBtn',
    'promptLayer', 'promptTitle', 'promptLabel', 'promptInput', 'promptOk', 'promptCancel',
    'profileBtn', 'profilesModal', 'profilesTitle', 'profilesList', 'profilesClose',
    'statsBtn', 'statsModal', 'statsTitle', 'statsBody', 'statsClose',
    'collectionsModal', 'collectionsTitle', 'collectionsList', 'collectionsClose', 'manageCollectionsBtn',
    'listFilter', 'regionFilter', 'resumeRow', 'resumeCards',
    'gameModalLater', 'gameModalCollections', 'gameModalOffline',
    'padTestModal', 'padTestList', 'padTestClose', 'padTestBtn',
    'deviceCheckModal', 'deviceCheckList', 'deviceCheckClose', 'deviceCheckCopy', 'deviceCheckBtn',
    'whatsNewModal', 'whatsNewTitle', 'whatsNewList', 'whatsNewClose', 'whatsNewBtn',
    'backupShareBtn', 'backupFolderBtn', 'folderBackupStatus', 'offlineList', 'stateImportInput',
    'libraryEditGenre', 'libraryEditYear', 'libraryEditPlayers', 'libraryEditModes',
  ].forEach((id) => { dom[id] = document.querySelector(`#${id}`); });
  dom.wipeEmulatorBtn = document.querySelector('#wipeEmulatorBtn');
  dom.clearAppCacheBtn = document.querySelector('#clearAppCacheBtn');
}

function bindEvents() {
  dom.searchInput.addEventListener('input', debounce((event) => {
    state.query = event.target.value.trim();
    updateSearchClearButton();
    applyFiltersAndRender();
  }, 180));

  // "✕" limpa a busca sem mexer nos demais filtros; Esc faz o mesmo
  // quando o campo está focado.
  dom.searchClearBtn.addEventListener('click', clearSearchOnly);
  dom.searchInput.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && dom.searchInput.value) {
      event.stopPropagation();
      clearSearchOnly();
    }
  });

  dom.genreFilter.addEventListener('change', (event) => {
    state.genre = event.target.value;
    applyFiltersAndRender();
  });

  dom.systemFilter.addEventListener('change', (event) => {
    state.system = event.target.value;
    applyFiltersAndRender();
  });

  dom.favoritesToggle.addEventListener('change', (event) => {
    state.favoritesOnly = event.target.checked;
    applyFiltersAndRender();
  });

  dom.sortSelect.addEventListener('change', (event) => {
    state.sort = event.target.value;
    applyFiltersAndRender();
  });

  dom.clearFiltersBtn.addEventListener('click', clearFilters);
  dom.emptyClearBtn.addEventListener('click', clearFilters);
  dom.backToCatalogBtn.addEventListener('click', scrollToCatalog);
  // Após um erro de ROM, "Voltar ao catálogo" também restaura o standby —
  // antes só rolava a página e o player ficava preso no estado de erro
  // (com `is-active` bloqueando a navegação por controle; ver showGameError).
  dom.gameErrorBackBtn.addEventListener('click', () => {
    resetPlayerToStandby();
    // Limpa o ?game= da URL: sem isto, um F5 depois do erro tentava a
    // mesma ROM quebrada de novo.
    setDeepLinkParam('');
    scrollToCatalog();
  });
  dom.fullscreenBtn.addEventListener('click', toggleFullscreen);
  dom.continueBtn.addEventListener('click', continueLastGame);
  if (dom.helpBtn) dom.helpBtn.addEventListener('click', openHelpModal);
  if (dom.helpBtnPlaceholder) dom.helpBtnPlaceholder.addEventListener('click', openHelpModal);
  if (dom.helpClose) dom.helpClose.addEventListener('click', closeHelpModal);
  if (dom.helpModal) {
    dom.helpModal.addEventListener('click', (event) => {
      if (event.target === dom.helpModal) closeHelpModal();
    });
  }
  if (dom.settingsBtn) dom.settingsBtn.addEventListener('click', openSettingsModal);
  if (dom.settingsClose) dom.settingsClose.addEventListener('click', closeSettingsModal);
  if (dom.settingsModal) {
    dom.settingsModal.addEventListener('click', (event) => {
      if (event.target === dom.settingsModal) closeSettingsModal();
    });
  }
  if (dom.settingsList) dom.settingsList.addEventListener('click', handleSettingsClick);
  if (dom.randomBtn) dom.randomBtn.addEventListener('click', playRandomGame);
  if (dom.installBtn) dom.installBtn.addEventListener('click', promptInstall);
  if (dom.heroPlayBtn) dom.heroPlayBtn.addEventListener('click', heroPlayAction);
  if (dom.saveStateBtn) dom.saveStateBtn.addEventListener('click', quickSaveCurrent);
  if (dom.loadStateBtn) dom.loadStateBtn.addEventListener('click', quickLoadCurrent);
  if (dom.statesBtn) dom.statesBtn.addEventListener('click', openStatesPanel);
  if (dom.statesClose) dom.statesClose.addEventListener('click', closeStatesPanel);
  if (dom.statesList) dom.statesList.addEventListener('click', handleStatesClick);
  if (dom.gameModalShare) dom.gameModalShare.addEventListener('click', shareCurrentGame);
  if (dom.playersFilter) {
    dom.playersFilter.addEventListener('change', () => {
      state.playersFilter = dom.playersFilter.value;
      applyFiltersAndRender();
    });
  }
  if (dom.importQueueClose) dom.importQueueClose.addEventListener('click', closeImportQueue);
  bindV21Events();
  if (dom.libraryEditClose) dom.libraryEditClose.addEventListener('click', closeLibraryEdit);
  if (dom.libraryEditSave) dom.libraryEditSave.addEventListener('click', submitLibraryEdit);
  if (dom.libraryMetaInput) {
    dom.libraryMetaInput.addEventListener('change', (event) => {
      const file = event.target.files && event.target.files[0];
      if (file) importLibraryMetadata(file);
      event.target.value = '';
    });
  }
  if (dom.withSaveToggle) {
    dom.withSaveToggle.addEventListener('change', () => {
      state.withSaveOnly = dom.withSaveToggle.checked;
      applyFiltersAndRender();
    });
  }
  if (dom.appVersionLabel) dom.appVersionLabel.textContent = CONFIG.appVersion;
  // Chip de status vira ação: com jogo aberto, clicar devolve o foco ao
  // iframe (a saída mais comum do estado "sem eventos").
  if (dom.gamepadStatus) {
    dom.gamepadStatus.addEventListener('click', () => {
      if (state.currentIframe) focusEmulatorFrame();
    });
  }
  // Biblioteca no painel de backup: remover, editar, lote, metadados.
  if (dom.localRomsList) {
    dom.localRomsList.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-local]');
      if (remove) { if (armButtonConfirm(remove)) removeLocalRomEntry(remove.dataset.removeLocal); return; }
      const edit = event.target.closest('[data-edit-local]');
      if (edit) { openLibraryEdit(edit.dataset.editLocal); return; }
      if (event.target.closest('[data-library-export]')) { exportLibraryMetadata(); return; }
      if (event.target.closest('[data-library-import]')) { if (dom.libraryMetaInput) dom.libraryMetaInput.click(); return; }
      const batch = event.target.closest('[data-remove-selected]');
      if (batch) {
        const ids = [...dom.localRomsList.querySelectorAll('[data-select-local]:checked')].map((el) => el.dataset.selectLocal);
        if (!ids.length) { showToast('Selecione as ROMs que quer remover.'); return; }
        openConfirmLayer({
          title: `Remover ${ids.length} ROM(s) da biblioteca?`,
          message: 'Os arquivos guardados neste navegador serão apagados. Saves não são afetados.',
          actions: [{ label: 'Remover', kind: 'danger', handler: () => removeLocalRomsBatch(ids) }],
        });
      }
    });
  }
  // Gestão dos dados do emulador (saves/BIOS/cache) — delegação.
  if (dom.emuDataList) {
    dom.emuDataList.addEventListener('click', (event) => handleEmuDataClick(event));
  }
  // Gestão das configurações do portal — delegação.
  if (dom.configList) {
    dom.configList.addEventListener('click', (event) => handleConfigClick(event));
  }
  if (dom.wipeEmulatorBtn) {
    dom.wipeEmulatorBtn.addEventListener('click', () => {
      if (!armButtonConfirm(dom.wipeEmulatorBtn)) return;
      wipeEmulatorData();
    });
  }
  if (dom.clearAppCacheBtn) {
    dom.clearAppCacheBtn.addEventListener('click', () => {
      if (!armButtonConfirm(dom.clearAppCacheBtn)) return;
      clearAppCache();
    });
  }
  dom.powerOffBtn.addEventListener('click', powerOffEmulator);
  dom.loadMoreBtn.addEventListener('click', renderNextCatalogPage);

  // Seleção de ROM local via botão/file picker — essencial no mobile,
  // onde não existe drag & drop.
  const openRomPicker = () => dom.romFileInput.click();
  dom.openRomBtn.addEventListener('click', openRomPicker);
  dom.openRomBtnPlaceholder.addEventListener('click', openRomPicker);
  dom.romFileInput.addEventListener('change', (event) => {
    const files = event.target.files ? [...event.target.files] : [];
    // Um arquivo: fluxo direto (detecta core, abre o jogo). Vários: fila de
    // importação em lote, com erro individual e deduplicação por hash.
    if (files.length === 1) handleDroppedRomFile(files[0]);
    else if (files.length > 1) importFilesBatch(files);
    event.target.value = ''; // permite escolher o mesmo arquivo de novo
  });

  // Atalho: "/" foca a busca (padrão de catálogos grandes).
  document.addEventListener('keydown', (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target;
    const isTyping = target instanceof HTMLElement
      && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
    if (isTyping) return;
    // Modais abertos têm a própria navegação (focus trap / setas).
    const modalOpen = document.body.classList.contains('has-modal');

    if (event.key === '/') {
      event.preventDefault();
      dom.searchInput.focus();
      dom.searchInput.select();
      return;
    }
    if (modalOpen) return;
    if (event.key === '?') { event.preventDefault(); openHelpModal(); return; }

    // Paridade com o controle: setas navegam os cards, Enter joga, F
    // favorita, Espaço abre detalhes. Antes só o Tab movia o foco.
    const arrows = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
    if (arrows[event.key]) {
      if (!state.filteredRoms.length) return;
      event.preventDefault();
      moveGpFocus(arrows[event.key]);
      return;
    }
    const card = currentGpCard();
    if (!card) return;
    if (event.key === 'Enter') {
      const play = card.querySelector('[data-play-id]');
      if (play) { event.preventDefault(); play.click(); }
      return;
    }
    if (event.key === ' ' || event.key === 'Spacebar') {
      const more = card.querySelector('[data-more-id]');
      if (more) { event.preventDefault(); more.click(); }
      return;
    }
    if (event.key === 'f' || event.key === 'F') {
      const fav = card.querySelector('[data-fav-id]');
      if (fav) { event.preventDefault(); fav.click(); }
    }
  });

  dom.viewCoverBtn.addEventListener('click', () => setView('cover'));
  dom.viewShotBtn.addEventListener('click', () => setView('screenshot'));

  dom.catalogGrid.addEventListener('click', (event) => {
    const favButton = event.target.closest('[data-fav-id]');
    if (favButton) {
      toggleFavorite(favButton.dataset.favId, favButton);
      return;
    }
    const moreButton = event.target.closest('[data-more-id]');
    if (moreButton) {
      const game = findGameById(moreButton.dataset.moreId);
      if (game) openGameModal(game);
      return;
    }
    const button = event.target.closest('[data-play-id]');
    if (!button) return;
    const game = findGameById(button.dataset.playId);
    if (game) playGame(game);
  });

  // Modal de detalhes
  dom.gameModalClose.addEventListener('click', closeGameModal);
  dom.gameModal.addEventListener('click', (event) => {
    if (event.target === dom.gameModal) closeGameModal();
  });
  dom.gameModalPlay.addEventListener('click', () => {
    const game = state.modalGame;
    closeGameModal();
    if (game) playGame(game);
  });
  dom.gameModalImage.addEventListener('error', () => {
    dom.gameModalImage.classList.add('is-hidden');
    dom.gameModalBackdrop.classList.add('is-hidden');
  });

  // Painel de saves & backup
  dom.backupBtn.addEventListener('click', openBackupModal);
  dom.backupClose.addEventListener('click', closeBackupModal);
  dom.backupModal.addEventListener('click', (event) => {
    if (event.target === dom.backupModal) closeBackupModal();
  });
  dom.backupExportBtn.addEventListener('click', exportBackup);
  dom.backupImportBtn.addEventListener('click', () => dom.backupFileInput.click());
  dom.backupFileInput.addEventListener('change', (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) startImportFlow(file);
    event.target.value = '';
  });

  dom.recentChips.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-recent-id]');
    if (!chip) return;
    const id = chip.dataset.recentId;
    if (isLocalRomId(id)) {
      continueLocalRom(id);
      return;
    }
    const game = findGameById(id);
    if (game) playGame(game);
  });

  dom.catalogGrid.addEventListener('error', handleCoverError, true);

  // Voltar ao topo + header condensado: um único listener passivo de
  // scroll, com rAF para não custar nada no rolar de 600+ cards.
  dom.scrollTopBtn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: smoothBehavior() });
  });
  let scrollTicking = false;
  window.addEventListener('scroll', () => {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(() => {
      scrollTicking = false;
      const y = window.scrollY || 0;
      dom.scrollTopBtn.classList.toggle('is-hidden', y < 600);
      dom.siteHeader.classList.toggle('is-condensed', y > 24);
    });
  }, { passive: true });

  setupLocalRomDragAndDrop();
}

/* ---------------------------------------------------------------------------
   Anel de confirmação do combo de saída (Select+Start no controle).
   O raio do círculo é 52 → circunferência 2·π·52 ≈ 326.7; animamos o
   stroke-dashoffset de cheio (vazio) a zero (completo).
--------------------------------------------------------------------------- */
var EXIT_RING_CIRCUMFERENCE = 2 * Math.PI * 52;

function updateExitRing(progress) {
  if (!dom.exitRing) return;
  var p = Math.max(0, Math.min(1, progress));
  if (p <= 0) {
    dom.exitRing.classList.add('is-hidden');
    return;
  }
  dom.exitRing.classList.remove('is-hidden');
  dom.exitRingProgress.style.strokeDasharray = String(EXIT_RING_CIRCUMFERENCE);
  dom.exitRingProgress.style.strokeDashoffset = String(EXIT_RING_CIRCUMFERENCE * (1 - p));
}

/* ---------------------------------------------------------------------------
   Foco do iframe do emulador: o Chromium só entrega dados de gamepad ao
   frame FOCADO. Depois que o controle dorme/acorda, o foco costuma estar
   na página pai — devolvê-lo ao iframe reabre o fluxo de botões.
--------------------------------------------------------------------------- */
function focusEmulatorFrame() {
  const iframe = state.currentIframe;
  if (!iframe) return;
  try { iframe.focus(); } catch (_) {}
  try { if (iframe.contentWindow) iframe.contentWindow.focus(); } catch (_) {}
}

// Se o foco programático não bastar (a página pai pode nem ter foco),
// o PRÓXIMO clique do usuário em qualquer lugar é convertido em foco no
// iframe — um gesto real sempre destrava o gating do navegador.
function onNextClickFocusFrame() {
  focusEmulatorFrame();
  disarmFocusForwarder();
}

function armFocusForwarder() {
  if (state.focusForwarderArmed) return;
  state.focusForwarderArmed = true;
  document.addEventListener('pointerdown', onNextClickFocusFrame, true);
}

function disarmFocusForwarder() {
  if (!state.focusForwarderArmed) return;
  state.focusForwarderArmed = false;
  document.removeEventListener('pointerdown', onNextClickFocusFrame, true);
}

function clearSearchOnly() {
  state.query = '';
  dom.searchInput.value = '';
  updateSearchClearButton();
  applyFiltersAndRender();
  dom.searchInput.focus();
}

function updateSearchClearButton() {
  dom.searchClearBtn.classList.toggle('is-hidden', !dom.searchInput.value);
}

async function loadCatalog() {
  state.isCatalogLoading = true;
  setCatalogError(null);

  try {
    const response = await fetch('roms.json', { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`roms.json retornou HTTP ${response.status}`);
    }

    const data = await response.json();
    const validated = validateRoms(data);

    if (!validated.length) {
      throw new Error('roms.json não possui jogos válidos.');
    }

    state.catalog = validated;
    announce('Catálogo carregado.');
  } catch (error) {
    console.warn('[EmuTab] Falha ao carregar roms.json. Usando catálogo de fallback.', error);
    state.catalog = validateRoms(fallbackRoms);
    showToast('Não consegui carregar roms.json. Usei um catálogo demonstrativo para manter a vitrine acesa.');
  } finally {
    state.isCatalogLoading = false;
    recomposeGames();
    syncFilterControls();
    updateStats();
    updateContinueButton();
    renderRecentGames();
  }
}

/** Lista exibida = catálogo remoto + cards da biblioteca local. Função
 *  única de recomposição: qualquer fonte que mude chama aqui. */
function recomposeGames() {
  const locals = [...state.localRoms.values()]
    .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
    .map(localRomAsGame);
  state.roms = (state.catalog || []).concat(locals);
  renderGenreOptions();
  renderSystemOptions();
  renderRegionOptions();
  applyFiltersAndRender();
}

/* ---------------------------------------------------------------------------
   Deep-link: ?game=<id> abre o jogo direto — atalho na área de trabalho,
   favorito do navegador ou link compartilhado. O parâmetro é mantido em
   sincronia: entra ao jogar, sai ao desligar o console.
--------------------------------------------------------------------------- */
function openDeepLinkedGame() {
  let id = '';
  try {
    id = new URLSearchParams(window.location.search).get('game') || '';
  } catch (_) { return; }
  if (!id) return;
  const game = findGameById(id);
  if (game) {
    // Sem gesto do usuário o autoplay de áudio seria bloqueado — o player
    // recebe autostart=0 e o EmulatorJS mostra o botão de play dele.
    playGame(game, { noAutostart: true });
  } else {
    showToast('O jogo do link não está no catálogo.');
    setDeepLinkParam('');
  }
}

function setDeepLinkParam(id) {
  try {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('game', id);
    else url.searchParams.delete('game');
    window.history.replaceState(null, '', url.toString());
  } catch (_) { /* noop */ }
}

function validateRoms(data) {
  if (!Array.isArray(data)) return [];

  const seenIds = new Set();
  return data
    .map((item, index) => normalizeRom(item, index))
    .filter(Boolean)
    .filter((game) => {
      // ids duplicados quebram recentes/Continuar/contadores (todos keyed
      // por id) — vence a primeira ocorrência, as demais são descartadas.
      if (seenIds.has(game.id)) {
        console.warn(`[EmuTab] roms.json: id duplicado ignorado: "${game.id}"`);
        return false;
      }
      seenIds.add(game.id);
      // Core desconhecido não é fatal (o EmulatorJS suporta mais sistemas do
      // que o seletor local lista), mas costuma ser typo — avisa no console.
      // LOCAL_CORE_LABELS é declarado mais adiante; aqui já foi inicializado,
      // pois validateRoms só roda após o fetch assíncrono do catálogo.
      if (!Object.prototype.hasOwnProperty.call(LOCAL_CORE_LABELS, game.core)) {
        console.warn(`[EmuTab] roms.json: core "${game.core}" fora da lista conhecida (jogo "${game.id}").`);
      }
      return true;
    });
}

function normalizeRom(item, index) {
  if (!item || typeof item !== 'object') return null;

  const id = String(item.id || slugify(item.title || `game-${index + 1}`)).trim();
  const title = String(item.title || '').trim();
  const romUrl = String(item.romUrl || '').trim();

  if (!id || !title || !romUrl) return null;

  return {
    id,
    title,
    description: String(item.description || 'Cartucho pronto para rodar no navegador.').trim(),
    year: String(item.year || '—').trim(),
    genre: String(item.genre || 'Other').trim(),
    system: String(item.system || 'NES').trim(),
    core: String(item.core || CONFIG.emulatorCore).trim(),
    featured: Boolean(item.featured),
    // Metadados novos (v1.6). players é o mais útil no sofá: "o que dá para
    // jogar em dois?". Todos são opcionais — catálogos antigos seguem válidos.
    players: Number(item.players) || 1,
    coop: Boolean(item.coop),
    modes: Array.isArray(item.modes) ? item.modes.map((m) => String(m).trim()).filter(Boolean) : [],
    region: String(item.region || '').trim(),
    tags: Array.isArray(item.tags) ? item.tags.map((tag) => String(tag).trim()).filter(Boolean) : [],
    added: String(item.added || '').trim(),
    cover: String(item.cover || '').trim(),
    screenshot: String(item.screenshot || '').trim(),
    romUrl,
  };
}

function renderGenreOptions() {
  const genres = [...new Set(state.roms.map((game) => game.genre).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  dom.genreFilter.innerHTML = '<option value="all">Todos</option>';

  genres.forEach((genre) => {
    const option = document.createElement('option');
    option.value = genre;
    option.textContent = genre;
    dom.genreFilter.appendChild(option);
  });
}

function renderSystemOptions() {
  const systems = [...new Set(state.roms.map((game) => game.system).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  dom.systemFilter.innerHTML = '<option value="all">Todos</option>';

  systems.forEach((system) => {
    const option = document.createElement('option');
    option.value = system;
    option.textContent = system;
    dom.systemFilter.appendChild(option);
  });
}

/** Reflete o estado (possivelmente restaurado do localStorage) nos controles. */
function syncFilterControls() {
  dom.searchInput.value = state.query;
  updateSearchClearButton();
  dom.sortSelect.value = state.sort;
  dom.favoritesToggle.checked = state.favoritesOnly;
  updateFavCountBadge();
  updateViewToggle();
  // Se o valor salvo não existir mais no catálogo, volta para "all".
  dom.genreFilter.value = state.genre;
  if (dom.genreFilter.value !== state.genre) state.genre = dom.genreFilter.value = 'all';
  dom.systemFilter.value = state.system;
  if (dom.systemFilter.value !== state.system) state.system = dom.systemFilter.value = 'all';
}

function updateFavCountBadge() {
  const count = state.favorites.size;
  dom.favCountBadge.textContent = String(count);
  dom.favCountBadge.classList.toggle('is-hidden', count === 0);
}

/* ---------------------------------------------------------------------------
   Modo de visualização do catálogo: capa (vertical) x tela (horizontal).
   Troca in-place — re-renderiza apenas o grid, sem reload, preservando
   filtros, busca e ordenação. A escolha persiste entre sessões.
--------------------------------------------------------------------------- */
function setView(view) {
  const next = view === 'screenshot' ? 'screenshot' : 'cover';
  if (next === state.view) return;
  state.view = next;
  updateViewToggle();
  persistFilters();
  state.renderLimit = 0;
  renderCatalog();
  announce(next === 'screenshot'
    ? 'Catálogo exibindo telas dos jogos.'
    : 'Catálogo exibindo capas dos jogos.');
}

function updateViewToggle() {
  const isShot = state.view === 'screenshot';
  dom.viewCoverBtn.classList.toggle('is-active', !isShot);
  dom.viewCoverBtn.setAttribute('aria-pressed', String(!isShot));
  dom.viewShotBtn.classList.toggle('is-active', isShot);
  dom.viewShotBtn.setAttribute('aria-pressed', String(isShot));
  dom.catalogGrid.classList.toggle('view-screenshot', isShot);
}

/* ---------------------------------------------------------------------------
   Modal de detalhes: descrição completa sem expandir o card.
--------------------------------------------------------------------------- */
function openGameModal(game) {
  state.modalGame = game;
  state.modalLastFocus = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;

  const image = getCardImage(game);
  dom.gameModalImage.classList.toggle('is-hidden', !image);
  dom.gameModalBackdrop.classList.toggle('is-hidden', !image);
  dom.gameModalImage.src = image || '';
  dom.gameModalBackdrop.src = image || '';
  dom.gameModalImage.alt = image ? `${state.view === 'screenshot' ? 'Tela' : 'Capa'} de ${game.title}` : '';
  dom.gameModal.querySelector('.game-modal-media').classList
    .toggle('is-screenshot', state.view === 'screenshot');

  dom.gameModalTitle.textContent = game.title;
  dom.gameModalDesc.textContent = game.description;

  dom.gameModalMeta.innerHTML = '';
  const modalMeta = [game.year, game.genre, game.system];
  if (game.players > 1) modalMeta.push(`👥 ${game.players} • ${describeModes(game)}`);
  if (game.region) modalMeta.push(game.region);
  (game.tags || []).forEach((tag) => modalMeta.push(`#${tag}`));
  if (hasSaveFor(game)) modalMeta.push('💾 tem save');
  const played = getPlayTime(game.id);
  if (played >= 60) modalMeta.push(`⏱ ${formatPlayTime(played)}`);
  modalMeta.forEach((value) => {
    const li = document.createElement('li');
    li.textContent = value;
    dom.gameModalMeta.appendChild(li);
  });

  updateGameModalExtras(game);
  dom.gameModal.classList.remove('is-hidden');
  layerOpened('game', dom.gameModalPlay);
}

function closeGameModal() {
  state.modalGame = null;
  dom.gameModal.classList.add('is-hidden');
  layerClosed('game');
}

function updateStats() {
  const genres = new Set(state.roms.map((game) => game.genre));
  const systems = new Set(state.roms.map((game) => game.system));
  dom.totalGamesStat.textContent = String(state.roms.length);
  dom.genresStat.textContent = String(genres.size);
  if (dom.systemsStat) dom.systemsStat.textContent = String(systems.size);
}

function applyFiltersAndRender() {
  const query = normalizeSearch(state.query);

  state.filteredRoms = state.roms
    .filter((game) => {
      const matchesGenre = state.genre === 'all' || game.genre === state.genre;
      const matchesSystem = state.system === 'all' || game.system === state.system;
      const matchesFavorites = !state.favoritesOnly || isFavorite(game.id);
      // Busca SEM a descrição: os textos descritivos citam franquias e
      // fabricantes ("sega", "nintendo", "mario") e poluíam os resultados —
      // buscar "sega" retornava dezenas de jogos pelo texto, não pelo nome.
      const searchable = normalizeSearch(`${game.title} ${game.year} ${game.genre} ${game.system} ${game.core} ${game.region} ${(game.tags || []).join(' ')}`);
      const matchesQuery = !query || searchable.includes(query);
      const matchesSave = !state.withSaveOnly || hasSaveFor(game);
      const matchesPlayers = matchesPlayersFilter(game);
      return matchesGenre && matchesSystem && matchesFavorites && matchesQuery && matchesSave && matchesPlayers
        && matchesListFilter(game) && matchesRegionFilter(game);
    })
    .sort(sortGames);

  if (query) {
    // Relevância por cima da ordenação escolhida (estável via índice):
    // título que COMEÇA com o termo vem antes, depois título que contém,
    // por fim matches em ano/gênero/sistema/core. "sonic" traz os Sonic
    // no topo mesmo com ordenação por ano.
    const rankOf = (game) => {
      const title = normalizeSearch(game.title);
      if (title.startsWith(query)) return 0;
      if (title.includes(query)) return 1;
      return 2;
    };
    state.filteredRoms = state.filteredRoms
      .map((game, index) => ({ game, index, rank: rankOf(game) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((entry) => entry.game);
  }

  state.renderLimit = 0; // recomeça a paginação a cada mudança de filtro
  persistFilters();
  renderCatalog();
}

function sortGames(a, b) {
  if (state.sort === 'title') {
    return a.title.localeCompare(b.title);
  }

  if (state.sort === 'year-desc') {
    return getYear(b) - getYear(a) || a.title.localeCompare(b.title);
  }

  if (state.sort === 'year-asc') {
    return getYear(a) - getYear(b) || a.title.localeCompare(b.title);
  }

  if (state.sort === 'most-time') {
    return (getPlayTime(b.id) - getPlayTime(a.id)) || a.title.localeCompare(b.title);
  }
  if (state.sort === 'recent-added') {
    // "added" vem do roms.json (data de entrada no catálogo); sem o campo,
    // cai para o ano do jogo para não embaralhar.
    const addedOf = (game) => (game.added ? Date.parse(game.added) || 0 : 0);
    return (addedOf(b) - addedOf(a)) || (getYear(b) - getYear(a)) || a.title.localeCompare(b.title);
  }
  if (state.sort === 'unplayed') {
    // Não jogados primeiro, depois os menos jogados.
    return (getPlayCount(a.id) - getPlayCount(b.id)) || a.title.localeCompare(b.title);
  }
  if (state.sort === 'most-played') {
    return getPlayCount(b.id) - getPlayCount(a.id)
      || Number(isFavorite(b.id)) - Number(isFavorite(a.id))
      || a.title.localeCompare(b.title);
  }

  // "featured": favoritos do usuário vêm antes de tudo, depois os
  // destaques definidos no catálogo. (O campo "favorite" de curadoria foi
  // aposentado na v1.3.0 — nunca foi usado e confundia com os favoritos do
  // usuário, que vivem no localStorage.)
  return Number(isFavorite(b.id)) - Number(isFavorite(a.id))
    || Number(b.featured) - Number(a.featured)
    || getYear(b) - getYear(a)
    || a.title.localeCompare(b.title);
}

function renderCatalog() {
  disconnectLoadMoreObserver();
  state.gpNavIndex = -1;
  dom.catalogGrid.innerHTML = '';
  setCatalogError(null);

  const total = state.filteredRoms.length;
  dom.catalogCount.textContent = total === 1 ? '1 jogo encontrado' : `${total} jogos encontrados`;
  dom.emptyState.classList.toggle('is-hidden', total > 0);
  dom.catalogGrid.classList.toggle('is-hidden', total === 0);
  dom.loadMoreWrap.classList.add('is-hidden');

  if (!total) {
    // Mensagem contextual: vazio por falta de favoritos é diferente de
    // vazio por filtro/busca sem resultado.
    if (state.favoritesOnly && state.favorites.size === 0) {
      dom.emptyStateTitle.textContent = 'Nenhum favorito ainda';
      dom.emptyStateText.textContent = 'Toque no coração de um card para montar sua coleção. Ela fica salva neste navegador.';
    } else {
      dom.emptyStateTitle.textContent = 'Nenhum cartucho encontrado';
      dom.emptyStateText.textContent = 'Tente remover filtros ou buscar por outro nome. O catálogo provavelmente está só fazendo pose dramática.';
    }
    announce('Nenhum jogo encontrado para os filtros atuais.');
    return;
  }

  renderNextCatalogPage();
}

/**
 * Renderiza o próximo lote de cards. Com 600+ jogos, criar tudo de uma vez
 * congela o layout — e o delay escalonado da animação (index × 45ms) faria
 * o último card aparecer meia hora depois. Lote + cap no índice resolvem.
 */
function renderNextCatalogPage() {
  const start = state.renderLimit;
  const end = Math.min(start + CONFIG.catalogPageSize, state.filteredRoms.length);
  if (start >= end) return;

  const fragment = document.createDocumentFragment();

  for (let index = start; index < end; index += 1) {
    const game = state.filteredRoms[index];
    const article = document.createElement('article');
    article.className = 'game-card';
    // Cap: só os primeiros cards de cada lote têm stagger perceptível.
    article.style.setProperty('--card-index', Math.min(index - start, 11));
    article.innerHTML = createGameCardMarkup(game);
    fragment.appendChild(article);
  }

  dom.catalogGrid.appendChild(fragment);
  state.renderLimit = end;

  const hasMore = state.renderLimit < state.filteredRoms.length;
  dom.loadMoreWrap.classList.toggle('is-hidden', !hasMore);
  dom.loadMoreBtn.textContent =
    `Carregar mais (${state.filteredRoms.length - state.renderLimit} restantes)`;

  if (hasMore) observeLoadMore();
}

function observeLoadMore() {
  disconnectLoadMoreObserver();
  if (!('IntersectionObserver' in window)) return; // fallback: botão manual

  state.loadMoreObserver = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      renderNextCatalogPage();
    }
  }, { rootMargin: '600px 0px' });

  state.loadMoreObserver.observe(dom.loadMoreWrap);
}

function disconnectLoadMoreObserver() {
  if (state.loadMoreObserver) {
    state.loadMoreObserver.disconnect();
    state.loadMoreObserver = null;
  }
}

// Descrições acima deste tamanho ganham o botão "Ler mais" (≈6 linhas
// na largura do card). O clamp CSS de 6 linhas é a garantia visual;
// este limiar só decide quando oferecer o modal.
const DESCRIPTION_MORE_THRESHOLD = 220;

/** Imagem do card conforme o modo de visualização, com fallback cruzado. */
function getCardImage(game) {
  if (state.view === 'screenshot') return game.screenshot || game.cover || '';
  return game.cover || game.screenshot || '';
}

function createGameCardMarkup(game) {
  const safeTitle = escapeHTML(game.title);
  const safeDescription = escapeHTML(game.description);
  const safeGenre = escapeHTML(game.genre);
  const safeYear = escapeHTML(game.year);
  const safeSystem = escapeHTML(game.system);
  const safeImage = escapeAttribute(getCardImage(game));
  const safeId = escapeAttribute(game.id);
  const favorited = isFavorite(game.id);
  const hasLongDescription = game.description.length > DESCRIPTION_MORE_THRESHOLD;

  return `
    <div class="cover">
      <div class="cover-fallback" aria-hidden="true">
        <div>
          <span>▧</span>
          <strong>${safeTitle}</strong>
        </div>
      </div>
      ${safeImage ? `
      ${hasSaveFor(game) ? '<span class="card-save-badge" title="Você tem um save deste jogo">💾</span>' : ''}
      ${isGameOffline(game) ? `<span class="card-offline-badge" title="${escapeAttribute(describeOfflineStatus(game))}">✈️</span>` : ''}
      <img class="cover-backdrop" src="${safeImage}" alt="" aria-hidden="true" loading="lazy" decoding="async" fetchpriority="low">
      <img class="cover-art" src="${safeImage}" alt="${state.view === 'screenshot' ? 'Tela' : 'Capa'} de ${safeTitle}" loading="lazy" decoding="async">` : ''}
      <div class="badges">
        <span class="badge">${safeSystem}</span>
        <span>
          ${game.featured ? '<span class="badge badge-featured">Destaque</span>' : ''}
        </span>
      </div>
      <button
        class="fav-toggle ${favorited ? 'is-active' : ''}"
        type="button"
        data-fav-id="${safeId}"
        aria-pressed="${favorited}"
        aria-label="${favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}: ${safeTitle}"
        title="${favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}"
      >${favorited ? '♥' : '♡'}</button>
    </div>
    <div class="game-body">
      <div class="game-info">
        <h3 class="game-title">${safeTitle}</h3>
        <p class="game-description">${safeDescription}</p>
        ${hasLongDescription ? `
        <button class="desc-more" type="button" data-more-id="${safeId}" aria-label="Ler descrição completa de ${safeTitle}">
          Ler mais
        </button>` : ''}
      </div>
      <ul class="game-meta" aria-label="Metadados do jogo">
        <li>${safeYear}</li>
        <li>${safeGenre}</li>
        <li>${safeSystem}</li>
        ${game.players > 1 ? `<li class="meta-players" title="${escapeAttribute(describeModes(game))} para ${game.players}">👥 ${game.players}${game.coop ? ' co-op' : ''}</li>` : ''}
        ${game.region ? `<li class="meta-region">${escapeHTML(game.region)}</li>` : ''}
      </ul>
      <button class="btn btn-primary" type="button" data-play-id="${safeId}">
        Jogar
        <span class="btn-sheen" aria-hidden="true"></span>
      </button>
    </div>
  `;
}

function renderCatalogSkeleton() {
  dom.catalogGrid.classList.remove('is-hidden');
  dom.emptyState.classList.add('is-hidden');
  dom.catalogCount.textContent = 'Carregando catálogo…';
  dom.catalogGrid.innerHTML = Array.from({ length: 6 }).map(() => `
    <article class="skeleton-card" aria-hidden="true">
      <div class="skeleton-media"></div>
      <div class="skeleton-lines">
        <span></span><span></span><span></span><span></span>
      </div>
    </article>
  `).join('');
}

/**
 * Porta única para abrir um jogo (catálogo ou biblioteca local). Passa pela
 * pergunta "quem é P1/P2" (multiplayer com ≥ 2 controles) e pela oferta de
 * retomar o progresso salvo; a sessão em si é do controlador (session.js).
 */
async function playGame(game, options = {}) {
  if (!game) return;
  if (!state.bootReady) { showToast('Carregando o catálogo…'); return; }
  const opts = Object.assign({}, options);
  if (!opts.playerOrder && shouldAskPlayerOrder(game)) {
    const order = await openPlayerAssignment(game);
    if (order) opts.playerOrder = order;
  }
  await startGameWithResume(game, opts);
}

async function preflightRom(romUrl) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), CONFIG.romPreflightTimeoutMs);

  try {
    const response = await fetch(romUrl, {
      method: 'HEAD',
      mode: 'cors',
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`A ROM retornou HTTP ${response.status}.`);
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('A validação da ROM demorou demais e foi interrompida.');
    }

    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * Destrói o iframe atual do emulador. Como o iframe é uma origem/contexto
 * independente, removê-lo do DOM faz o navegador descartar canvas, WebGL,
 * AudioContext, web workers e todas as variáveis EJS_* — sem precisar de
 * EJS_terminate (que é instável entre versões do EmulatorJS).
 */
function destroyEmulatorIframe() {
  if (state.messageHandler) {
    window.removeEventListener('message', state.messageHandler);
    state.messageHandler = null;
  }

  if (state.currentIframe) {
    try {
      // Sobrescreve src antes de remover ajuda alguns navegadores a
      // liberarem áudio mais rápido.
      state.currentIframe.src = 'about:blank';
    } catch (_) { /* noop */ }

    if (state.currentIframe.parentNode) {
      state.currentIframe.parentNode.removeChild(state.currentIframe);
    }
    state.currentIframe = null;
  }

  if (dom.emulatorMount) {
    dom.emulatorMount.innerHTML = '';
  }
}

function clearGameTimers() {
  if (state.gameLoadTimer) {
    window.clearTimeout(state.gameLoadTimer);
    state.gameLoadTimer = null;
  }
}

function updatePlayerInfo(game) {
  dom.selectedGameTitle.textContent = game.title;
  dom.selectedGameMeta.textContent = `${game.genre} • ${game.year} • ${game.system} • ${game.core}`;
}

function showGameLoading(text) {
  state.isGameLoading = true;
  dom.gameLoadingText.textContent = text;
  dom.gameLoading.classList.remove('is-hidden');
  dom.gameError.classList.add('is-hidden');
}

function hideGameLoading() {
  state.isGameLoading = false;
  clearGameTimers();
  dom.gameLoading.classList.add('is-hidden');
}

function resetGameError() {
  dom.gameError.classList.add('is-hidden');
  dom.gameErrorText.textContent = '';
}

function showGameError(message, diag) {
  hideGameLoading();
  dom.powerOffBtn.classList.add('is-hidden');
  if (dom.gameErrorActions) {
    dom.gameErrorActions.innerHTML = '';
    (diag && diag.actions ? diag.actions : []).forEach((action) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-ghost';
      button.textContent = action.label;
      button.addEventListener('click', () => action.handler());
      dom.gameErrorActions.appendChild(button);
    });
  }
  // CRÍTICO para o modo sofá: sem remover `is-active`, isGamepadNavAllowed()
  // bloqueava a navegação por controle PARA SEMPRE após qualquer erro de
  // ROM — e na TV não há mouse para destravar.
  dom.emulatorMount.classList.remove('is-active');
  dom.playerPlaceholder.classList.remove('is-hidden');
  document.body.classList.remove('is-playing');
  state.gpNavPrimed = false;
  dom.gameErrorText.textContent = message;
  dom.gameError.classList.remove('is-hidden');
  announce('Erro ao carregar ROM.');
}

/**
 * "Desliga" o console: destrói o iframe (libera CPU, áudio e memória) e
 * devolve o player ao estado standby. Antes disso, a única forma de parar
 * um jogo era carregar outro ou recarregar a página.
 */
/** Devolve o player ao standby — caminho único usado pelo Desligar e pela
 *  recuperação de erro (antes cada um restaurava um subconjunto do estado). */
async function resetPlayerToStandby() {
  updateExitRing(0);
  await endSession('reset');
  hideGameLoading();
  resetGameError();
  state.selectedGame = null;
  document.body.classList.remove('is-playing');
  dom.emulatorMount.classList.remove('is-active');
  dom.playerPlaceholder.classList.remove('is-hidden');
  dom.powerOffBtn.classList.add('is-hidden');
  dom.selectedGameTitle.textContent = 'Aguardando cartucho';
  dom.selectedGameMeta.textContent = 'Nenhuma ROM carregada ainda.';
  setStateButtonsVisible(false);
}

async function powerOffEmulator() {
  // endSession faz o autosave (quando suportado e ligado), fecha o tempo de
  // jogo e destrói o iframe — o mesmo caminho da troca de jogo e do pagehide.
  await resetPlayerToStandby();
  setDeepLinkParam('');
  announce('Emulador encerrado.');
  showToast('Console desligado. Escolha outro cartucho quando quiser.');

  // Devolve o foco à janela do portal: ao destruir o iframe, o foco fica
  // órfão e document.hasFocus() pode retornar false, bloqueando a
  // navegação por controle logo após sair pelo Select+Start.
  try { window.focus(); } catch (_) {}
  try { if (document.body) document.body.focus(); } catch (_) {}
  // Novo priming na volta: qualquer botão ainda segurado ao sair do jogo
  // não pode virar comando fantasma no catálogo.
  state.gpNavPrimed = false;
}

function createFriendlyRomError(error) {
  const base = 'Verifique se a URL da ROM está correta, se o arquivo existe, se não há bloqueio por CORS/hotlink e se o servidor permite leitura por outro site.';
  const detail = error?.message ? ` Detalhe técnico: ${error.message}` : '';
  return `${base}${detail}`;
}

function continueLastGame() {
  const lastGameId = localStorage.getItem(CONFIG.localStorageLastGameKey);
  if (!lastGameId) return;
  if (!state.bootReady) {
    // Fontes ainda carregando: espera em vez de concluir que o jogo sumiu.
    showToast('Carregando o catálogo… tente de novo em instantes.');
    return;
  }

  // ROM local salva na biblioteca (IndexedDB): reabre pelo File guardado.
  if (isLocalRomId(lastGameId)) {
    if (!state.localRoms.has(lastGameId)) {
      showToast('A ROM local do último jogo não está mais neste navegador.');
      return;
    }
    playGame(localRomAsGame(state.localRoms.get(lastGameId)));
    return;
  }

  const game = findGameById(lastGameId);
  if (!game) {
    localStorage.removeItem(CONFIG.localStorageLastGameKey);
    updateContinueButton();
    showToast('O último jogo salvo não existe mais no catálogo. Limpei esse atalho para você.');
    return;
  }

  playGame(game);
}

function saveLastGame(gameId) {
  localStorage.setItem(CONFIG.localStorageLastGameKey, gameId);
}

function updateContinueButton() {
  updateHeroCta();
  const lastGameId = localStorage.getItem(CONFIG.localStorageLastGameKey);
  const game = lastGameId
    ? (isLocalRomId(lastGameId) ? state.localRoms.get(lastGameId) : findGameById(lastGameId))
    : null;
  dom.continueBtn.classList.toggle('is-hidden', !game);
  if (game) {
    // Nome do jogo direto no botão: "Continuar último jogo" genérico não
    // dizia nada; agora o usuário sabe o que vai abrir.
    // O rótulo vive num span próprio: é ele que ganha ellipsis dinâmica no
    // CSS quando o header aperta (botão flex não ellipsiza texto solto).
    // v2.1: com ponto salvo, a miniatura da partida e "parou há X".
    const resume = continueResumeInfo(lastGameId);
    const icon = resume && resume.thumbnail
      ? `<img class="continue-thumb" src="${resume.thumbnail}" alt="" aria-hidden="true">`
      : '<span class="btn-icon" aria-hidden="true">▶</span>';
    dom.continueBtn.innerHTML =
      `${icon} <span class="btn-label">Continuar: ${escapeHTML(truncate(game.title, 26))}</span>`;
    dom.continueBtn.title = resume
      ? `Continuar ${game.title} — ponto salvo ${resume.when} (${resume.slot})`
      : `Continuar ${game.title}`;
  }
}

function truncate(value, max) {
  const text = String(value || '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function clearFilters(options) {
  state.withSaveOnly = false;
  if (dom.withSaveToggle) dom.withSaveToggle.checked = false;
  state.playersFilter = 'all';
  if (dom.playersFilter) dom.playersFilter.value = 'all';
  state.listFilter = 'all';
  if (dom.listFilter) dom.listFilter.value = 'all';
  state.regionFilter = 'all';
  if (dom.regionFilter) dom.regionFilter.value = 'all';
  // Chamado como handler de clique (recebe o Event) ou pelo painel de dados
  // com { focusSearch: false } — dentro do modal, focar a busca jogaria o
  // foco para trás do painel aberto.
  const focusSearch = !(options && options.focusSearch === false);
  state.query = '';
  state.genre = 'all';
  state.system = 'all';
  state.favoritesOnly = false;
  state.sort = 'featured';
  dom.searchInput.value = '';
  updateSearchClearButton();
  dom.genreFilter.value = 'all';
  dom.systemFilter.value = 'all';
  dom.favoritesToggle.checked = false;
  dom.sortSelect.value = 'featured';
  applyFiltersAndRender();
  if (focusSearch) dom.searchInput.focus();
}

function scrollToCatalog() {
  document.querySelector('#catalog').scrollIntoView({ behavior: smoothBehavior(), block: 'start' });
}

async function toggleFullscreen() {
  if (!document.fullscreenEnabled && !document.webkitFullscreenEnabled) {
    showToast('Este navegador não liberou Fullscreen API para esta página.');
    return;
  }

  try {
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
    if (fullscreenElement) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else if (dom.playerFrame.requestFullscreen) {
      await dom.playerFrame.requestFullscreen();
    } else if (dom.playerFrame.webkitRequestFullscreen) {
      dom.playerFrame.webkitRequestFullscreen();
    }
  } catch (error) {
    showToast('Não consegui ativar a tela cheia. O navegador bloqueou a ação.');
  }
}

function updateFullscreenButton() {
  const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
  const isFullscreen = Boolean(fullscreenElement);
  dom.fullscreenBtn.setAttribute('aria-pressed', String(isFullscreen));
  dom.fullscreenBtn.textContent = isFullscreen ? 'Sair da tela cheia' : 'Tela cheia';
}

function handleCoverError(event) {
  const image = event.target;
  if (!(image instanceof HTMLImageElement)) return;
  const cover = image.closest('.cover');
  if (cover) cover.classList.add('is-broken');
  image.alt = 'Capa indisponível';
}

function setCatalogError(message) {
  if (!message) {
    dom.catalogError.classList.add('is-hidden');
    dom.catalogErrorText.textContent = '';
    return;
  }

  dom.catalogErrorText.textContent = message;
  dom.catalogError.classList.remove('is-hidden');
}

function findGameById(id) {
  return state.roms.find((game) => game.id === id);
}

function getYear(game) {
  // "198X"/"199X" (década incerta) viravam 198/199 no parseInt e afundavam
  // na ordenação por ano. Trocamos o X por 5 (meio da década) só para o sort.
  const normalized = String(game.year || '').replace(/x/gi, '5');
  const year = Number.parseInt(normalized, 10);
  return Number.isFinite(year) ? year : 0;
}

function buildAbsoluteUrl(url) {
  try {
    return new URL(url, window.location.href).href;
  } catch (_) {
    throw new Error('URL da ROM inválida.');
  }
}

function normalizeSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function slugify(value) {
  return normalizeSearch(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'game';
}

function escapeHTML(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function escapeAttribute(value) {
  return escapeHTML(value).replaceAll('`', '&#096;');
}

function debounce(callback, delay = 180) {
  let timer;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => callback(...args), delay);
  };
}

/**
 * Toast com fila e ação opcional:
 *   showToast('mensagem')
 *   showToast('mensagem', { action: { label: 'Fazer X', handler: fn } })
 * A fila evita que mensagens em rajada se atropelem (comum na retomada de
 * controle Bluetooth); a ação vira um botão dentro do toast.
 */
function showToast(message, options = {}) {
  state.toastQueue.push({ message, action: options.action || null, action2: options.action2 || null });
  if (!state.toastShowing) {
    dequeueToast();
    return;
  }
  // Já há toast em tela: encurta o tempo restante do ATUAL para a fila
  // andar — antes, a mensagem corrente segurava seus 5,2s inteiros e a
  // seguinte esperava além do necessário.
  window.clearTimeout(state.toastTimer);
  state.toastTimer = window.setTimeout(dequeueToast, 2600);
}

function dequeueToast() {
  window.clearTimeout(state.toastTimer);
  const item = state.toastQueue.shift();
  if (item === undefined) {
    state.toastShowing = false;
    dom.toast.classList.add('is-hidden');
    return;
  }
  state.toastShowing = true;
  // announce na EXIBIÇÃO (não no enfileiramento): o leitor de tela recebe
  // cada mensagem no ritmo em que ela aparece, sem uma sobrescrever a outra.
  announce(item.message);
  dom.toast.textContent = item.message;
  [item.action, item.action2].filter(Boolean).forEach((action, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = index === 0 ? 'toast-action' : 'toast-action toast-action-alt';
    button.textContent = action.label;
    button.addEventListener('click', () => {
      try { action.handler(); } catch (_) { /* ação é melhor esforço */ }
      dequeueToast();
    });
    dom.toast.appendChild(button);
  });
  dom.toast.classList.remove('is-hidden');
  // Toast com ação fica mais tempo em tela (o usuário precisa alcançar o
  // botão); sem ação, o ritmo depende da fila.
  const duration = (item.action || item.action2) ? 12000 : (state.toastQueue.length ? 3400 : 5200);
  state.toastTimer = window.setTimeout(dequeueToast, duration);
}

/** "Cooperativo, versus, simultâneo" — legenda humana dos modos de jogo. */
function describeModes(game) {
  const labels = { coop: 'cooperativo', versus: 'versus', simultaneous: 'simultâneo', alternating: 'revezamento' };
  const modes = (game.modes || []).map((m) => labels[m]).filter(Boolean);
  if (!modes.length) return game.coop ? 'cooperativo' : 'multijogador';
  return modes.join(', ');
}


/* ===========================================================================
   EVENTOS v2.1
   ======================================================================== */
function bindV21Events() {
  const on = (el, type, fn) => { if (el) el.addEventListener(type, fn); };
  on(dom.pauseActions, 'click', handlePauseClick);
  on(dom.pauseMenuBtn, 'click', () => openPauseMenu());
  on(dom.promptOk, 'click', submitPrompt);
  on(dom.promptCancel, 'click', cancelPrompt);
  on(dom.promptInput, 'keydown', (event) => {
    if (event.key === 'Enter') { event.preventDefault(); submitPrompt(); }
  });
  on(dom.profileBtn, 'click', () => openProfilesPanel());
  on(dom.profilesList, 'click', handleProfilesClick);
  on(dom.profilesClose, 'click', closeProfilesPanel);
  on(dom.statsBtn, 'click', openStatsPanel);
  on(dom.statsClose, 'click', closeStatsPanel);
  on(dom.statsBody, 'click', handleStatsClick);
  on(dom.collectionsList, 'click', handleCollectionsClick);
  on(dom.collectionsClose, 'click', closeCollectionsPanel);
  on(dom.manageCollectionsBtn, 'click', () => openCollectionsPanel(null));
  on(dom.listFilter, 'change', () => { state.listFilter = dom.listFilter.value; applyFiltersAndRender(); });
  on(dom.regionFilter, 'change', () => { state.regionFilter = dom.regionFilter.value; applyFiltersAndRender(); });
  on(dom.resumeCards, 'click', handleResumeClick);
  on(dom.gameModalLater, 'click', () => {
    if (!state.modalGame) return;
    const later = togglePlayLater(state.modalGame.id);
    updateGameModalExtras(state.modalGame);
    showToast(later ? `⏳ "${truncate(state.modalGame.title, 30)}" na fila para jogar depois.` : 'Removido da fila.');
  });
  on(dom.gameModalCollections, 'click', () => { if (state.modalGame) openCollectionsPanel(state.modalGame); });
  on(dom.gameModalOffline, 'click', () => { if (state.modalGame) toggleGameOffline(state.modalGame); });
  on(dom.padTestBtn, 'click', openPadTest);
  on(dom.padTestClose, 'click', closePadTest);
  on(dom.deviceCheckBtn, 'click', openDeviceCheck);
  on(dom.deviceCheckClose, 'click', closeDeviceCheck);
  on(dom.deviceCheckCopy, 'click', copyDeviceCheck);
  on(dom.whatsNewBtn, 'click', openWhatsNew);
  on(dom.whatsNewClose, 'click', closeWhatsNew);
  on(dom.backupShareBtn, 'click', shareBackup);
  on(dom.backupFolderBtn, 'click', handleBackupFolderButton);
  on(dom.offlineList, 'click', handleOfflineListClick);
  on(dom.stateImportInput, 'change', (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) importStateFile(file);
    event.target.value = '';
  });
  if (dom.backupShareBtn && !canShareFiles()) dom.backupShareBtn.classList.add('is-hidden');
}

// @generated-exports
export { DESCRIPTION_MORE_THRESHOLD, EXIT_RING_CIRCUMFERENCE, REGION_LABELS, applyFiltersAndRender, armFocusForwarder, bindEvents, bindV21Events, buildAbsoluteUrl, cacheDom, clearFilters, clearGameTimers, clearSearchOnly, closeGameModal, continueLastGame, createFriendlyRomError, createGameCardMarkup, debounce, dequeueToast, describeModes, destroyEmulatorIframe, disarmFocusForwarder, disconnectLoadMoreObserver, escapeAttribute, escapeHTML, findGameById, focusEmulatorFrame, getCardImage, getYear, handleCoverError, hideGameLoading, init, initialized, isPortugueseGame, loadCatalog, matchesRegionFilter, normalizeRom, normalizeSearch, observeLoadMore, onNextClickFocusFrame, openDeepLinkedGame, openGameModal, playGame, powerOffEmulator, preflightRom, recomposeGames, renderCatalog, renderCatalogSkeleton, renderGenreOptions, renderNextCatalogPage, renderRegionOptions, renderSystemOptions, resetGameError, resetPlayerToStandby, runBootPrompts, saveLastGame, scrollToCatalog, setCatalogError, setDeepLinkParam, setView, showGameError, showGameLoading, showToast, slugify, sortGames, syncFilterControls, toggleFullscreen, truncate, updateContinueButton, updateExitRing, updateFavCountBadge, updateFullscreenButton, updatePlayerInfo, updateSearchClearButton, updateStats, updateViewToggle, validateRoms, whenLayerClosed };
// @generated-exports
